import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Stage, Layer, Rect, Group, Path, Circle } from 'react-konva';
import { dataURLtoFile } from '../builder/ImageUploader.js';

import { colorToArray, darkenMainColorByPercent, isEmpty, isNotEmpty } from '../../lib/ep.js';

const real_board_size = { w:600, h:600 };	// px
const default_cache_props = { pixelRatio:2, imageSmoothingEnabled:true, offset:15, drawBorder:false };
const outter_padding = 30;
const default_team_size = { w:50, h:50 };
const default_logo_size = 44;

const getBracketScale = (num_rounds) => {
	if(num_rounds == 2) return 1.8;
	if(num_rounds == 3) return 1.2;
	return 1;
};

const getMatchTeamsMinSepartion = (num_rounds) => {
	const bracket_scale = getBracketScale(num_rounds);

	const divider_x = (num_rounds == 2) ? 1.5 : 1;
	const divider_y = (num_rounds == 2) ? 1.2 : 1;

	const num_column_teams = 2 * num_rounds;
	const teams_total_w = default_team_size.w * num_column_teams * bracket_scale;
	const stage_usable_w = real_board_size.w - 2 * outter_padding;
	const x_sep = (stage_usable_w - teams_total_w) / (num_column_teams - 1) / divider_x;
	
	const num_teams_by_side_first_round = Math.pow(2, num_rounds) / 2;
	const teams_total_h = default_team_size.h * num_teams_by_side_first_round * bracket_scale;
	const stage_usable_h = real_board_size.h - 2 * outter_padding;
	const y_sep = (stage_usable_h - teams_total_h) / (num_teams_by_side_first_round - 1) / divider_y;

	return {
		x:x_sep,
		y:y_sep,
	};
};

export const calcBracketHeight = (num_rounds, count_padding = true, in_game = false) => {
	const effective_rounds = Math.pow(2, num_rounds) / 2;
	const bracket_scale = getBracketScale(num_rounds);
	const match_teams_min_separation = getMatchTeamsMinSepartion(num_rounds);
	const total_padding = (count_padding) ? 2 * outter_padding : 0;
	const team_h = ((in_game && false) ? 0.5 : 1) * bracket_scale * default_team_size.h;

	return total_padding + team_h * effective_rounds + match_teams_min_separation.y * (effective_rounds - 1);
};

const Wrapper = forwardRef((props, ref) => {
	const is_standalone = props?.is_standalone ?? false;

	const refs = useRef([]);
	const stage_ref = useRef();

	useImperativeHandle(ref, () => ({
		redraw() {
			refs.current['layer'].draw();
		},

		to_image(format = 'file') {
			if(stage_ref.current != undefined) {
				let dataURL = stage_ref.current.toDataURL({
					mimeType:'image/jpeg',
					quality:0.8,
					pixelRatio:3,
				});

				if(format == 'base64') return dataURL;
				const file = dataURLtoFile(dataURL, 'filename.jpg');
				return file;
			}
		}
	}), []);

	if(is_standalone === false) {
		return (
			<>{props.children}</>
		);
	}

	const canvas_size = props?.canvas_size ?? real_board_size;
	const display_outter_border = props?.display_outter_border ?? true;

	const main_classes = ['canvas_cont'];
	if(display_outter_border === false) main_classes.push('not_bordered');

	return (
		<Stage
			ref={stage_ref}
			className={main_classes.join(" ")}
			width={canvas_size.w}
			height={canvas_size.h}
		>
			<Layer
				ref={(node) => { refs.current['layer'] = node; }}
				scaleX={canvas_size.w / real_board_size.w}
				scaleY={canvas_size.h / real_board_size.h}
			>
				{props.children}
			</Layer>
		</Stage>
	)
});

const OneTimeBracketCanvas = forwardRef((props, ref) => {
	const stage = props?.stage?.Stage ?? (props?.stage ?? {});

	const [ full_loaded, setFullLoaded ] = useState(false);

	const indexed_images = props?.indexed_images ?? {};

	const is_builder = props?.is_builder ?? false;
	const is_test = props?.is_test ?? false;
	const in_game = props?.in_game ?? false;
	const is_standalone = props?.is_standalone ?? false;

	const matches = props?.matches ?? (stage?.guess_score_matches ?? []);
	const display_outter_border = stage?.display_outter_border ?? true;

	const display_winner_dot = false;

	const rounds = parseInt(stage?.guess_score_num_rounds ?? 4);
	const bracket_scale = getBracketScale(rounds);
	const match_teams_min_separation = getMatchTeamsMinSepartion(rounds);
	const logo_size = default_logo_size * bracket_scale;

	const default_bg_color = props.promotion?.Promotion?.layout_props?.color?.background_color ?? 'rgba(255, 255, 255, 1)';
	const default_text_color = props.promotion?.Promotion?.layout_props?.color?.text_color ?? 'rgba(0, 0, 0, 1)';
	const default_main_color = props.promotion?.Promotion?.layout_props?.color?.main_color ?? 'rgba(30, 34, 170, 1)';

	const bg_color = stage?.guess_score_bracket_bg_color ?? darkenMainColorByPercent(0.2, stage?.bg_color ?? default_bg_color);
	const team_border_radius = parseInt(stage?.guess_score_team_border_radius ?? '0');
	const winner_bg_color = stage?.guess_score_winner_bg_color ?? `rgba(${colorToArray(stage?.main_color ?? default_main_color).join(",")}, 0.2)`;
	const winner_line_color = stage?.guess_score_winner_line_color ?? (stage?.main_color ?? default_main_color);
	const loser_bg_color = stage?.guess_score_loser_bg_color ?? `rgba(${colorToArray(stage?.text_color ?? default_text_color).join(",")}, 0.2)`;
	const loser_line_color = stage?.guess_score_loser_line_color ?? 'rgba(150, 150, 150, 1)';
	const loser_grayscale = stage?.guess_score_loser_grayscale ?? true;
	const pending_bg_color = stage?.guess_score_pending_bg_color ?? `rgba(${colorToArray(stage?.text_color ?? default_text_color).join(",")}, 0.1)`;
	const pending_line_color = stage?.guess_score_pending_line_color ?? `rgba(${colorToArray(stage?.text_color ?? default_text_color).join(",")}, 0.3)`;

	const user_answer = props?.user_answer?.guess_score ?? (props?.answers ?? {});

	const winner_line_width = 2.5;
	const loser_line_width = 1;

	const refs = useRef([]);
	const size = useRef({ w:real_board_size.w, h:real_board_size.h });
	const user_bets = useRef([]);
	const team_size = useRef({ w:bracket_scale * default_team_size.w, h:bracket_scale * default_team_size.h });
	const actual_match_index = useRef(0);
	const block_winner_selection = useRef(true);

	const winner_size_multiplier = 1.75;

	const generateUserBets = (cb) => {
		const bets = [];
		let current_match_index = 0;

		matches.map((match, m) => {
			if(isEmpty(match, 'id_1') && isEmpty(match, 'id_2')) return;
			if(m > Math.pow(2, rounds) / 2 - 1) return;

			bets.push({
				match_id:match.id,
				winner:(is_builder && !is_test && m == 0) ? match.id_1 : (user_answer[match.id]?.winner ?? false),
				id_1:match.id_1,
				id_2:match.id_2,
			});

			current_match_index++;
		});

		let matches_in_prev_round = 0;

		{[...Array(rounds - 1).keys()].map((round) => {
			if(round > 0) matches_in_prev_round += Math.pow(2, rounds - round);
			const matches_in_round = Math.pow(2, rounds - (round + 1)) / 2;

			[...Array(matches_in_round).keys()].map((match) => {
				const match_index = 2 * match + matches_in_prev_round;

				if(bets[match_index].winner !== false) {
					bets.push({
						match_id:matches[current_match_index].id,
						winner:user_answer[matches[current_match_index].id]?.winner ?? false,
						id_1:bets[match_index].winner,
						id_2:bets[match_index + 1].winner,
					});
				} else {
					bets.push({
						match_id:matches[current_match_index].id,
						winner:user_answer[current_match_index]?.winner ?? false,
						id_1:'',
						id_2:'',
					});
				}

				current_match_index++;
			})
		})}

		user_bets.current = bets;
		if(cb != undefined) cb();
	};

	const highlightCurrentMatch = (match_index = 0) => {
		if(in_game === false) return;
		if(is_builder && !is_test) return;

		const match_key = `match_border_${match_index}`;
		const prev_match_key = `match_border_${match_index - 1}`;

		if(refs.current[match_key] == undefined) return;
		if(match_index == matches.length) return;

		const dash_unit = refs.current[match_key].attrs.dash_unit;

		if(refs.current[prev_match_key] != undefined) {
			refs.current[prev_match_key].to({
				opacity:0,
				duration:0.2,
				easing:Konva.Easings.StrongEaseInOut,
			});
		}

		refs.current[match_key].setAttrs({
			dashOffset:dash_unit,
			dash:[dash_unit, dash_unit],
			opacity:1,
		});

		refs.current[match_key].to({
			dashOffset:-1 * dash_unit,
			duration:1.2,
			easing:Konva.Easings.StrongEaseInOut,
			onFinish:() => {
				setTimeout(() => {
					if(refs.current[match_key] == undefined) return;

					refs.current[match_key].setAttrs({
						dashOffset:dash_unit,
					});

					refs.current[match_key].to({
						dashOffset:-1 * dash_unit,
						duration:1.2,
						easing:Konva.Easings.StrongEaseInOut,
						onFinish:() => {
							if(refs.current[match_key] == undefined) return;

							refs.current[match_key].to({
								opacity:0,
								duration:0.2,
								easing:Konva.Easings.StrongEaseInOut,
							});
						},
					});
				}, 100);
			},
		});
	};

	const gameInit = () => {
		if(in_game) {
			size.current.w = props?.forced_w ?? size_received.w;
			size.current.h = props?.forced_h ?? size_received.h;
			team_size.current.h = bracket_scale * default_team_size.h;

			setTimeout(() => {
				mountBoard();
			}, 0);

			setFullLoaded(true);

			setTimeout(() => {
				generateCache();
			}, 100);
			return;
		}
		
		if(props.adaptToContainer != undefined) {
			props.adaptToContainer((size_received) => {
				size.current.w = size_received;
				size.current.h = size_received;

				setTimeout(() => {
					mountBoard();
				}, 0);

				setFullLoaded(true);

				setTimeout(() => {
					generateCache();
				}, 100);
			});
			return;
		}

		setFullLoaded(true);
	};

	const resetGame = () => {
		generateUserBets();
		setFullLoaded(false);
		gameInit();
	};

	useEffect(() => {
		generateCache();
	}, [
		stage.text_color,
		stage.guess_score_bracket_bg_image,
		stage.guess_score_bracket_bg_color,
	]);

	useEffect(() => {
		if((props.user_answer?.guess_score ?? false) === false) return;

		setFullLoaded(false);
		generateUserBets(() => {
			setFullLoaded(true);
		});
	}, [
		props.user_answer?.guess_score,
	]);

	useEffect(() => {
		resetGame();
	}, []);

	const generateCache = () => {
		if(refs.current['bg'] == undefined) return;

		refs.current['bg'].cache(default_cache_props);

		const num_matches_in_first_round = Math.pow(2, rounds) / 2 - 1;

		[...Array(Math.pow(2, rounds) - 1).keys()].map((match) => {
			if(is_standalone === false && match > num_matches_in_first_round) return;
			
			refs.current[`logo_${match}_1`].cache(default_cache_props);
			refs.current[`logo_${match}_2`].cache(default_cache_props);

			if(is_standalone && (!is_builder || is_test)) {
				if(loser_grayscale) {
					const loser_index = (user_bets.current[match].winner == user_bets.current[match].id_1) ? 2 : 1;

					refs.current[`logo_${match}_${loser_index}`].setAttrs({
						saturation:-50,
					});
				}

				refs.current[`team_${match}_1`].cache(default_cache_props);
				refs.current[`team_${match}_2`].cache(default_cache_props);
			}
		});

		if(is_standalone) {
			refs.current['whole_canvas'].redraw();
		} else {
			props.redrawCanvas()
		}
	};

	const mountBoard = () => {
		setTimeout(() => {
			highlightCurrentMatch(0);
		}, 2000);
	};

	const animateSingleFinalWinnerStrokes = (stroke_index) => {
		const final_scale = 1.35;
		const double_final_scale = 1.4;

		if(refs.current[`effect_final_winner_${stroke_index}`] == undefined) return;
		refs.current[`effect_final_winner_${stroke_index}`].to({
			scaleX:final_scale,
			scaleY:final_scale,
			strokeWidth:0.01,
			cornerRadius:winner_size_multiplier * team_size.current.w * team_border_radius * double_final_scale * final_scale / 100,
			opacity:0,
			duration:2.2,
			easing:Konva.Easings.StrongEaseOut,
			onFinish:() => {
				if(refs.current[`effect_final_winner_${stroke_index}`] == undefined) return;

				refs.current[`effect_final_winner_${stroke_index}`].setAttrs({
					scaleX:1,
					scaleY:1,
					strokeWidth:winner_line_width,
					cornerRadius:winner_size_multiplier * team_size.current.w * team_border_radius / 100,
					opacity:1,
				});

				animateSingleFinalWinnerStrokes(stroke_index);
			},
		});
	};

	const animateFinalWinnerStrokes = () => {
		animateSingleFinalWinnerStrokes('1');

		setTimeout(() => {
			animateSingleFinalWinnerStrokes('2');
		}, '400');

		setTimeout(() => {
			animateSingleFinalWinnerStrokes('3');
		}, '800');
	};

	const getConnectorPoints = (round = 1, team_separation, direction = 1, first_team = true) => {
		const is_last_round = (round < rounds - 1) ? false : true;
		const is_prelast_round = (round == rounds - 2) ? true : false;

		let final_line_x;
		let final_line_y;
		let line_x;
		let line_y;
		let default_points_v;
		let default_points;
		let default_winner_points;

		if(is_last_round === false) {
			let extra_x = 0;
			if(rounds == 2) extra_x = -5;
			if(rounds == 3) extra_x = 5;
			if(rounds == 4) extra_x = -5;
			final_line_x = direction * (team_size.current.w + extra_x);

			let extra_y = 0;
			final_line_y = ((first_team) ? 1 : -1) * team_separation / 2 + extra_y;

			if(is_prelast_round) {
				if(rounds == 2) final_line_y = final_line_y - team_size.current.h / 2 - 15;
				if(rounds == 3) final_line_y = final_line_y - (team_size.current.h + match_teams_min_separation.y) / 2;
				if(rounds == 4) final_line_y = final_line_y - (team_size.current.h + match_teams_min_separation.y);
			}

			line_x = team_size.current.w / 2;
			if(first_team) line_y = team_size.current.h;

			default_points_v = [ `0,0`, `0,${final_line_y}` ];
			default_winner_points = [ ...default_points_v, `${final_line_x},${final_line_y}` ];
			default_points = (first_team) ? default_points_v : default_winner_points;
		} else {
			let extra_x = 0;
			final_line_x = direction * match_teams_min_separation.x / 2 + extra_x;

			let extra_y = 0;
			if(rounds == 2) extra_y = 7;
			if(rounds > 2) extra_y = 24;
			final_line_y = team_size.current.h + extra_y;

			if(first_team) line_x = team_size.current.w;
			line_y = team_size.current.h / 2;

			default_points_v = [ `0,0`, `${final_line_x},0` ];
			default_winner_points = [ ...default_points_v, `${final_line_x},${final_line_y}` ];
			default_points = (first_team) ? default_points_v : default_winner_points;
		}

		return {
			line_x,
			line_y,
			line_points:default_points,
			line_points_v:default_points_v,
			line_points_winner:default_winner_points,
			line_length:Math.abs(final_line_y) + Math.abs(final_line_x),
		}
	};

	const selectWinnerByClick = (index) => {
		if(is_builder && !is_test) return;
		if(block_winner_selection.current && actual_match_index.current > 0) return;

		const [ _match, _team ] = index.split("_");
		const match_index = parseInt(_match);
		const team_index = parseInt(_team);

		if(actual_match_index.current != match_index) return;

		if(props.onSelectWinnerFromCanvas != undefined) props.onSelectWinnerFromCanvas(team_index);
	};

	const selectWinner = (match_index, winner) => {
		block_winner_selection.current = true;

		const loser = (winner > 1) ? 1 : 2;

		const winner_ref_key = `${match_index}_${winner}`;
		const loser_ref_key = `${match_index}_${loser}`;

		refs.current[`match_border_${actual_match_index.current - 1}`].to({
			opacity:0,
			duration:0.2,
			easing:Konva.Easings.StrongEaseInOut,
		});

		const winner_next_match = Math.ceil((match_index + 1) / 2) + Math.pow(2, rounds) / 2 - 1;
		const winner_next_match_pos = (Math.ceil((match_index + 1) / 2) == (match_index + 1) / 2) ? 2 : 1;

		let img_key;
		let img;
		let image_scale;
		let is_last = false;

		if(refs.current[`logo_${winner_next_match}_${winner_next_match_pos}`] != undefined) {
			img_key = `logo_${matches[match_index][`id_${winner}`]}_small`;
			img = (isNotEmpty(indexed_images, img_key)) ? indexed_images[img_key] : false;
			image_scale = (img !== false) ? Math.min((logo_size / img.image_w), (logo_size / img.image_h)) : 1;

			refs.current[`logo_${winner_next_match}_${winner_next_match_pos}`].setAttrs({
				fillPatternImage:img.fillPatternImage,
				fillPatternScaleX:image_scale,
				fillPatternScaleY:image_scale,
				fillPriority:(img !== false) ? 'pattern' : 'fill',
				opacity:0,
			});

			refs.current[`logo_${winner_next_match}_${winner_next_match_pos}`].cache(default_cache_props);
			
			if(props.createNextMatch !== undefined) {
				props.createNextMatch(winner_next_match, winner_next_match_pos, matches[match_index][`id_${winner}`]);
			}
		} else if(refs.current['logo_final_winner'] != undefined) {
			img_key = `logo_${matches[match_index][`id_${winner}`]}`;
			img = (isNotEmpty(indexed_images, img_key)) ? indexed_images[img_key] : false;
			image_scale = (img !== false) ? Math.min((winner_size_multiplier * logo_size / img.image_w), (winner_size_multiplier * logo_size / img.image_h)) : 1;
			is_last = true;

			refs.current['logo_final_winner'].setAttrs({
				fillPatternImage:img.fillPatternImage,
				fillPatternScaleX:image_scale,
				fillPatternScaleY:image_scale,
				fillPriority:(img !== false) ? 'pattern' : 'fill',
				opacity:0,
			});

			refs.current['bg_logo_final_winner'].setAttrs({
				fill:winner_bg_color,
			});
		
			refs.current['outline_final_winner'].setAttrs({
				strokeWidth:winner_line_width,
				stroke:winner_line_color,
			});

			refs.current['logo_final_winner'].cache(default_cache_props);
		}

		refs.current[`connector_${winner_ref_key}`].to({
			opacity:0,
			duration:0.4,
			easing:Konva.Easings.StrongEaseInOut,
		});

		if(refs.current[`connector_v_${winner_ref_key}`] != undefined) {
			refs.current[`connector_v_${winner_ref_key}`].to({
				opacity:0,
				duration:0.4,
				easing:Konva.Easings.StrongEaseInOut,
			});
		}

		if(display_winner_dot) {
			refs.current[`dot_${winner_ref_key}`].setAttrs({
				opacity:1,
			});
		}

		refs.current[`bg_logo_${winner_ref_key}`].setAttrs({
			fill:winner_bg_color,
		});

		refs.current[`outline_${winner_ref_key}`].setAttrs({
			strokeWidth:winner_line_width,
			stroke:winner_line_color,
		});

		refs.current[`connector_winner_${winner_ref_key}`].setAttrs({
			opacity:1,
		});

		if(refs.current[`connector_v_${loser_ref_key}`] != undefined) {
			refs.current[`connector_v_${loser_ref_key}`].setAttrs({
				opacity:1,
				stroke:loser_line_color,
			});

			refs.current[`connector_${loser_ref_key}`].to({
				opacity:0,
				duration:0.4,
				easing:Konva.Easings.StrongEaseInOut,
			});
		} else {
			refs.current[`connector_${loser_ref_key}`].setAttrs({
				opacity:1,
				stroke:loser_line_color,
			});
		}

		refs.current[`connector_winner_${loser_ref_key}`].setAttrs({
			opacity:0,
		});

		refs.current[`bg_logo_${loser_ref_key}`].setAttrs({
			fill:loser_bg_color,
		});

		refs.current[`outline_${loser_ref_key}`].setAttrs({
			strokeWidth:loser_line_width,
			stroke:loser_line_color,
		});

		if(display_winner_dot) {
			refs.current[`dot_${loser_ref_key}`].setAttrs({
				opacity:0,
			});
		}

		if(loser_grayscale) {
			refs.current[`logo_${loser_ref_key}`].to({
				saturation:-50,
				duration:0.7,
				easing:Konva.Easings.StrongEaseOut,
				onFinish:() => {
					if(refs.current[`team_${loser_ref_key}`] == undefined) return;
					refs.current[`team_${loser_ref_key}`].cache(default_cache_props);
				},
			});
		} else {
			refs.current[`team_${loser_ref_key}`].cache(default_cache_props);
		}

		const _key = (is_last) ? 'logo_final_winner' : `logo_${winner_next_match}_${winner_next_match_pos}`;

		refs.current[`connector_winner_${winner_ref_key}`].to({
			dashOffset:0,
			duration:0.4,
			easing:Konva.Easings.StrongEaseInOut,
			onFinish:() => {
				setTimeout(() => {
					refs.current[_key].to({
						opacity:1,
						duration:0.4,
						easing:Konva.Easings.StrongEaseOut,
						onFinish:() => {
							if(is_last) {
								refs.current['bg_logo_final_winner'].setAttrs({
									fill:winner_bg_color,
								});
							
								refs.current['outline_final_winner'].setAttrs({
									strokeWidth:winner_line_width,
									stroke:winner_line_color,
								});
							}

							refs.current[_key].cache(default_cache_props);

							if(refs.current[`team_${winner_ref_key}`] == undefined) return;
							refs.current[`team_${winner_ref_key}`].cache(default_cache_props);
						},
					});
				}, 150);
			},
		});

		setTimeout(() => {
			highlightCurrentMatch(match_index + 1);
		}, 300);

		if(is_last) {
			animateFinalWinnerStrokes();
			return;
		}

		if(props.createNextMatch !== undefined) {
			props.createNextMatch(winner_next_match, winner_next_match_pos, matches[match_index][`id_${winner}`]);
		}
	};

	useImperativeHandle(ref, () => ({
		selectMatchWinner(match_index, winner) {
			actual_match_index.current++;
			selectWinner(match_index, winner);
		},

		increaseMatch() {
			block_winner_selection.current = false;
		},

		to_image(format = 'file') {
			return refs.current['whole_canvas'].to_image(format);
		},
	}), []);

	if(!full_loaded) return null;

	const bg_image = indexed_images?.bracket_bg_image ?? false;
	const bg_image_scale = (bg_image !== false) ? Math.min((real_board_size.w / bg_image.image_w), (real_board_size.h / bg_image.image_h)) : 1;

	let match_separation_previous = match_teams_min_separation.y;
	let match_y_previous = 0;
	let actual_team = 0;
	let actual_match = -1;

	const all_rounds_w = team_size.current.w * rounds + match_teams_min_separation.x * (rounds - 1);
	const all_bracket_w = 2 * all_rounds_w + match_teams_min_separation.x;
	const all_bracket_h = all_bracket_w;
	//const all_bracket_h = calcBracketHeight(rounds, false, false);

	const final_winner_user_bet = user_bets.current[Math.pow(2, rounds) - 2];

	let final_winner_img = false;
	let final_winner_image_scale = 1;
	if(final_winner_user_bet.winner !== false) {
		if(isNotEmpty(indexed_images, `logo_${final_winner_user_bet.winner}_small`)) {
			final_winner_img = indexed_images[`logo_${final_winner_user_bet.winner}_small`];
			final_winner_image_scale = Math.min((winner_size_multiplier * logo_size / final_winner_img.image_w), (winner_size_multiplier * logo_size / final_winner_img.image_h));
		}
	}

	const bracket_position = { x:0, y:0 };
	let final_winner_extra_y = 47;
	switch(rounds) {
		case 2:
			bracket_position.x = 2 * outter_padding;
			bracket_position.y = 2 * outter_padding;
			final_winner_extra_y = 116;
			break;

		case 3:
			bracket_position.x = outter_padding + ((is_standalone === false) ? (size.current.w - 2 * outter_padding - all_bracket_w) / 2 : 0);
			bracket_position.y = outter_padding + ((is_standalone === false) ? (size.current.h - 2 * outter_padding - all_bracket_h) / 2 : 0);
			final_winner_extra_y = 57.5;
			break;

		case 4:
			bracket_position.x = outter_padding + ((is_standalone === false) ? (size.current.w - 2 * outter_padding - all_bracket_w) / 2 : 0);
			bracket_position.y = outter_padding + ((is_standalone === false) ? (size.current.h - 2 * outter_padding - all_bracket_h) / 2 : 0);
			break;
	}

	const is_touch_device = ((window?.platform ?? 'desktop') == 'mobile') ? true : false;
	const prevent_default = (is_touch_device) ? false : null;

	return (
		<Wrapper
			ref={(node) => { refs.current['whole_canvas'] = node; }}
			is_standalone={is_standalone}
			display_outter_border={display_outter_border}
			canvas_size={size.current}
		>
			<Group
				ref={(node) => { refs.current['bg'] = node; }}
			>
				<Rect
					width={real_board_size.w}
					height={real_board_size.h}
					fill={bg_color}
					preventDefault={prevent_default}
				/>

				<Rect
					width={real_board_size.w}
					height={real_board_size.h}
					fillPatternImage={(bg_image !== false) ? bg_image.fillPatternImage : null}
					fillPatternScaleX={bg_image_scale}
					fillPatternScaleY={bg_image_scale}
					preventDefault={prevent_default}
				/>
			</Group>

			<Group
				ref={(node) => { refs.current['teams_c'] = node; }}
				{...bracket_position}
			>
				{[...Array(rounds).keys()].map((round) => {
					const is_last_round = (round < rounds - 1) ? false : true;

					const teams_in_round = Math.pow(2, rounds - round);

					const team_separation = round * (match_teams_min_separation.y + team_size.current.h) + match_separation_previous;
					const match_h = Math.max(2, round) * team_size.current.h + team_separation;

					const round_x = (team_size.current.w + match_teams_min_separation.x) * round;

					let y_displ = 0;
					let round_y = (match_h - team_size.current.h) / 2 - (match_teams_min_separation.y + team_size.current.h) / 2;
					if(is_last_round) {
						if(rounds == 2) y_displ = -1 * (match_teams_min_separation.y + team_size.current.h) + 3 * team_size.current.h / 2;
						if(rounds == 4) y_displ = match_y_previous;

						round_y = y_displ + match_teams_min_separation.y + team_size.current.h;
					}

					match_separation_previous = team_separation;
					match_y_previous = round_y;

					return (
						<Group
							key={`round_${round}`}
							x={round_x}
							y={round_y}
						>
							{[...Array(teams_in_round).keys()].map((team) => {
								const first_team = (team % 2 == 0) ? true : false;
								const is_left_area = (team < teams_in_round / 2) ? true : false;

								if(first_team) actual_match++;

								const user_bet = user_bets.current[actual_match];

								const team_x = (is_left_area) ? 0 : (all_bracket_w - 2 * round_x - team_size.current.w);
								const team_y = (team_size.current.h + team_separation) * (team % (teams_in_round / 2));

								let is_match_winner = false;
								let has_match_winner = false;
								if((user_bet?.winner ?? false) !== false) {
									has_match_winner = true;

									if(user_bet.winner == user_bet.id_1 && first_team) {
										is_match_winner = true;
									} else if(user_bet.winner == user_bet.id_2 && first_team === false) {
										is_match_winner = true;
									}
								}

								actual_team++;

								const direction = (is_left_area) ? 1 : -1;

								const { line_x, line_y, line_points, line_points_v, line_points_winner, line_length } = getConnectorPoints(round, team_separation, direction, first_team);

								let img_key = false;
								let img = false;

								if(user_bet != undefined) {
									img_key = user_bet[`id_${(first_team) ? '1' : '2'}`];

									if(isNotEmpty(indexed_images, `logo_${img_key}_small`)) {
										img = indexed_images[`logo_${img_key}_small`];
									}
								}

								const image_scale = (img !== false) ? Math.min((logo_size / img.image_w), (logo_size / img.image_h)) : 1;

								const team_index = (first_team) ? 1 : 2;
								const team_ref_key = `${actual_match}_${team_index}`;
								const match_ref_key = `match_border_${actual_match}`;

								let whole_match_w = team_size.current.w + 2 * match_teams_min_separation.x / 4;
								let whole_match_h = 2 * team_size.current.h + team_separation + 2 * match_teams_min_separation.x / 4;
								if(is_last_round) {
									whole_match_w = 2 * team_size.current.w + match_teams_min_separation.x + 2 * match_teams_min_separation.x / 4;
									whole_match_h = team_size.current.w + 2 * match_teams_min_separation.x / 4;
								}

								const whole_match_perimeter = 2 * (whole_match_w + whole_match_h);

								return (
									<Group
										key={`team_${actual_team}`}
										ref={(node) => { refs.current[`team_${team_ref_key}`] = node; }}
										x={team_x}
										y={team_y}
										preventDefault={prevent_default}
									>
										{(first_team) && (
											<Rect
												ref={(node) => { refs.current[match_ref_key] = node; }}
												width={whole_match_w}
												height={whole_match_h}
												x={-match_teams_min_separation.x / 4}
												y={-match_teams_min_separation.x / 4}
												fill='transparent'
												stroke={winner_line_color}
												strokeWidth={3}
												lineCap='round'
												lineJoin='round'
												dash_unit={whole_match_perimeter}
												dash={[whole_match_perimeter, whole_match_perimeter]}
												dashOffset={whole_match_perimeter}
												preventDefault={prevent_default}
											/>
										)}

										<Rect
											ref={(node) => { refs.current[`bg_logo_${team_ref_key}`] = node; }}
											width={team_size.current.w}
											height={team_size.current.h}
											fill={(is_match_winner) ? winner_bg_color : ((has_match_winner) ? loser_bg_color : pending_bg_color)}
											cornerRadius={team_size.current.w * team_border_radius / 100}
											preventDefault={prevent_default}
										/>

										<Rect
											ref={(node) => { refs.current[`logo_${team_ref_key}`] = node; }}
											width={logo_size}
											height={logo_size}
											fillPatternImage={(img !== false) ? img.fillPatternImage : null}
											fillPatternScaleX={image_scale}
											fillPatternScaleY={image_scale}
											fillPriority={(img !== false) ? 'fillPattern' : 'fill'}
											x={(team_size.current.w - logo_size) / 2}
											y={(team_size.current.h - logo_size) / 2}
											opacity={(img != false) ? 1 : 0}
											filters={[Konva.Filters.HSL]}
											preventDefault={prevent_default}
										/>

										<Path
											ref={(node) => { refs.current[`connector_${team_ref_key}`] = node; }}
											data={`M${line_points.join("L")}`}
											x={line_x}
											y={line_y}
											strokeWidth={loser_line_width}
											stroke={(has_match_winner) ? loser_line_color : pending_line_color}
											opacity={(is_match_winner || (has_match_winner && first_team)) ? 0 : 1}
											preventDefault={prevent_default}
										/>

										{(line_points_v !== false) && (
											<Path
												ref={(node) => { refs.current[`connector_v_${team_ref_key}`] = node; }}
												data={`M${line_points_v.join("L")}`}
												x={line_x}
												y={line_y}
												strokeWidth={loser_line_width}
												stroke={(has_match_winner) ? loser_line_color : pending_line_color}
												opacity={(is_match_winner || (has_match_winner && first_team)) ? 1 : 0}
												preventDefault={prevent_default}
											/>
										)}

										<Path
											ref={(node) => { refs.current[`connector_winner_${team_ref_key}`] = node; }}
											data={`M${line_points_winner.join("L")}`}
											x={line_x}
											y={line_y}
											strokeWidth={winner_line_width}
											stroke={winner_line_color}
											dash={[line_length, line_length]}
											dashOffset={(is_match_winner) ? 0 : line_length}
											opacity={(is_match_winner || (has_match_winner && first_team)) ? 1 : 0}
											preventDefault={prevent_default}
										/>

										<Rect
											ref={(node) => { refs.current[`outline_${team_ref_key}`] = node; }}
											width={team_size.current.w}
											height={team_size.current.h}
											strokeWidth={(is_match_winner) ? winner_line_width : loser_line_width}
											stroke={(is_match_winner) ? winner_line_color : ((has_match_winner) ? loser_line_color : pending_line_color)}
											cornerRadius={team_size.current.w * team_border_radius / 100}
											preventDefault={prevent_default}
										/>

										{(display_winner_dot) && (
											<Circle
												ref={(node) => { refs.current[`dot_${team_ref_key}`] = node; }}
												radius={winner_line_width + 1}
												fill={winner_line_color}
												x={(is_last_round) ? ((first_team) ? team_size.current.w : 0) : (team_size.current.w / 2)}
												y={(is_last_round) ? (team_size.current.h / 2) : ((first_team) ? team_size.current.h : 0)}
												opacity={(is_match_winner) ? 1 : 0}
												preventDefault={prevent_default}
											/>
										)}

										<Rect
											ref={(node) => { refs.current[`hover_${team_ref_key}`] = node; }}
											width={team_size.current.w}
											height={team_size.current.h}
											fill='yellow'
											opacity={0}
											preventDefault={prevent_default}
											onClick={() => selectWinnerByClick(team_ref_key)}
											onTap={() => selectWinnerByClick(team_ref_key)}
										/>
									</Group>
								)
							})}
						</Group>
					)
				})}
			</Group>

			<Group
				ref={(node) => { refs.current['final_winner'] = node; }}
				x={real_board_size.w / 2}
				y={real_board_size.h / 2 + final_winner_extra_y}
				preventDefault={prevent_default}
			>
				<Rect
					ref={(node) => { refs.current['effect_final_winner_1'] = node; }}
					width={winner_size_multiplier * team_size.current.w}
					height={winner_size_multiplier * team_size.current.h}
					strokeWidth={2 * winner_line_width}
					stroke={winner_line_color}
					fill='transparent'
					cornerRadius={winner_size_multiplier * team_size.current.w * team_border_radius / 100}
					opacity={0}
					offsetX={winner_size_multiplier * team_size.current.w / 2}
					offsetY={winner_size_multiplier * team_size.current.h / 2}
					preventDefault={prevent_default}
				/>

				<Rect
					ref={(node) => { refs.current['effect_final_winner_2'] = node; }}
					width={winner_size_multiplier * team_size.current.w}
					height={winner_size_multiplier * team_size.current.h}
					strokeWidth={winner_line_width}
					stroke={winner_line_color}
					fill='transparent'
					cornerRadius={winner_size_multiplier * team_size.current.w * team_border_radius / 100}
					opacity={0}
					offsetX={winner_size_multiplier * team_size.current.w / 2}
					offsetY={winner_size_multiplier * team_size.current.h / 2}
					preventDefault={prevent_default}
				/>

				<Rect
					ref={(node) => { refs.current['effect_final_winner_3'] = node; }}
					width={winner_size_multiplier * team_size.current.w}
					height={winner_size_multiplier * team_size.current.h}
					strokeWidth={winner_line_width}
					stroke={winner_line_color}
					fill='transparent'
					cornerRadius={winner_size_multiplier * team_size.current.w * team_border_radius / 100}
					opacity={0}
					offsetX={winner_size_multiplier * team_size.current.w / 2}
					offsetY={winner_size_multiplier * team_size.current.h / 2}
					preventDefault={prevent_default}
				/>

				<Rect
					ref={(node) => { refs.current['bg_logo_final_winner'] = node; }}
					width={winner_size_multiplier * team_size.current.w}
					height={winner_size_multiplier * team_size.current.h}
					fill={(final_winner_img !== false) ? winner_bg_color : pending_bg_color}
					cornerRadius={winner_size_multiplier * team_size.current.w * team_border_radius / 100}
					offsetX={winner_size_multiplier * team_size.current.w / 2}
					offsetY={winner_size_multiplier * team_size.current.h / 2}
					preventDefault={prevent_default}
				/>

				<Rect
					ref={(node) => { refs.current['logo_final_winner'] = node; }}
					width={winner_size_multiplier * logo_size}
					height={winner_size_multiplier * logo_size}
					opacity={(final_winner_img !== false) ? 1 : 0}
					offsetX={winner_size_multiplier * logo_size / 2}
					offsetY={winner_size_multiplier * logo_size / 2}
					fillPatternImage={(final_winner_img !== false) ? final_winner_img.fillPatternImage : null}
					fillPatternScaleX={final_winner_image_scale}
					fillPatternScaleY={final_winner_image_scale}
					preventDefault={prevent_default}
				/>

				<Rect
					ref={(node) => { refs.current['outline_final_winner'] = node; }}
					width={winner_size_multiplier * team_size.current.w}
					height={winner_size_multiplier * team_size.current.h}
					strokeWidth={(final_winner_img !== false) ? winner_line_width : loser_line_width}
					stroke={(final_winner_img !== false) ? winner_line_color : pending_line_color}
					cornerRadius={winner_size_multiplier * team_size.current.w * team_border_radius / 100}
					offsetX={winner_size_multiplier * team_size.current.w / 2}
					offsetY={winner_size_multiplier * team_size.current.h / 2}
					preventDefault={prevent_default}
				/>
			</Group>
		</Wrapper>
	)
});

export default OneTimeBracketCanvas;